import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createChatMessage,
  createDM,
  getChats,
  getOrganizationChats,
  getUserConnections,
  searchUsers,
} from "../../api/api";
import { sortChatsByRecent } from "../messaging_page/chatListUtils";
import { defaultGroupPfpUrl, defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { ExpandingTextarea } from "../ui/expanding-textarea";
import {
  buildContentShareUrl,
  buildShareMetadata,
  displayPersonName,
  getShareOrgIds,
  peerIdFromConnection,
} from "./shareUtils";

/**
 * @param {object} chat
 * @param {string} query
 * @returns {boolean}
 */
function chatMatchesQuery(chat, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = (chat.name || "").toLowerCase();
  const members = (chat.other_members || [])
    .map((member) => (member.name || member.username || "").toLowerCase())
    .join(" ");
  return name.includes(q) || members.includes(q);
}

/**
 * User ids already represented by a DM in the share list.
 * @param {Array<object>} chats
 * @returns {Set<number>}
 */
function dmPeerIds(chats) {
  const ids = new Set();
  for (const chat of chats || []) {
    if (chat.chat_type !== "dm") continue;
    const peer = chat.other_members?.[0];
    const peerId = Number(peer?.user_id);
    if (Number.isFinite(peerId)) ids.add(peerId);
  }
  return ids;
}

/**
 * Compact overlapping member avatars for group chats.
 * @param {{ chat: object, selected: boolean }} props
 * @returns {JSX.Element}
 */
function GroupShareAvatar({ chat, selected }) {
  const members = chat.other_members?.slice(0, 3) || [];
  if (members.length === 0) {
    return (
      <ShareCircleAvatar
        src={chat.image_url || defaultGroupPfpUrl}
        fallback={chat.name}
        fallbackSrc={defaultGroupPfpUrl}
        selected={selected}
      />
    );
  }
  const containerSize = 56;
  const imageSize = 32;
  const center = containerSize / 2;
  const radius = 12;
  return (
    <div className="relative" style={{ width: containerSize, height: containerSize }}>
      {members.map((member, index) => {
        const angle = (index * 2 * Math.PI) / members.length - Math.PI / 2;
        const x = center + radius * Math.cos(angle) - imageSize / 2;
        const y = center + radius * Math.sin(angle) - imageSize / 2;
        return (
          <Avatar
            key={member.user_id || index}
            className="absolute ring-2 ring-ui-card"
            style={{
              width: imageSize,
              height: imageSize,
              left: x,
              top: y,
              zIndex: index + 1,
            }}
          >
            <AvatarImage
              src={member.pfp_url || member.pfp || defaultPfpUrl}
              alt=""
              onError={(event) => {
                event.target.src = defaultPfpUrl;
              }}
            />
            <AvatarFallback className="text-[10px]">
              {(member.name || "?").slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        );
      })}
      {selected ? <SelectionCheck /> : null}
    </div>
  );
}

/**
 * @param {{ src: string, fallback: string, fallbackSrc?: string, selected?: boolean, orgSrc?: string, orgName?: string }} props
 * @returns {JSX.Element}
 */
function ShareCircleAvatar({
  src,
  fallback,
  fallbackSrc = defaultPfpUrl,
  selected = false,
  orgSrc = "",
  orgName = "",
}) {
  const letter = (fallback || "?").slice(0, 1).toUpperCase();
  return (
    <div className="relative h-14 w-14 shrink-0">
      <Avatar className="h-14 w-14">
        <AvatarImage
          src={src || fallbackSrc}
          alt=""
          onError={(event) => {
            event.target.src = fallbackSrc;
          }}
        />
        <AvatarFallback>{letter}</AvatarFallback>
      </Avatar>
      {orgSrc ? (
        <Avatar className="absolute bottom-0 left-0 h-5 w-5 ring-2 ring-ui-card">
          <AvatarImage
            src={orgSrc}
            alt={orgName || "Organization"}
            onError={(event) => {
              event.target.src = defaultPfpUrl;
            }}
          />
          <AvatarFallback className="text-[8px]">
            {(orgName || "?").slice(0, 1).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ) : null}
      {selected ? <SelectionCheck /> : null}
    </div>
  );
}

/**
 * @returns {JSX.Element}
 */
function SelectionCheck() {
  return (
    <span
      className="absolute bottom-0 right-0 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-text-on-accent ring-2 ring-ui-card"
      aria-hidden
    >
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

/**
 * Avatar for a share grid target.
 * @param {{ item: object, selected: boolean }} props
 * @returns {JSX.Element}
 */
function ShareTargetAvatar({ item, selected }) {
  if (item.kind === "connection") {
    return (
      <ShareCircleAvatar
        src={item.user?.pfp_url || defaultPfpUrl}
        fallback={item.name}
        selected={selected}
      />
    );
  }
  const chat = item.chat;
  const isOrgChannel = Boolean(chat?.org_id);
  if (!isOrgChannel && chat?.chat_type === "group") {
    return <GroupShareAvatar chat={chat} selected={selected} />;
  }
  return (
    <ShareCircleAvatar
      src={chat?.image_url || (chat?.chat_type === "group" ? defaultGroupPfpUrl : defaultPfpUrl)}
      fallback={item.name}
      fallbackSrc={chat?.chat_type === "group" ? defaultGroupPfpUrl : defaultPfpUrl}
      selected={selected}
      orgSrc={isOrgChannel ? chat.org_image_url || defaultPfpUrl : ""}
      orgName={isOrgChannel ? chat.org_name : ""}
    />
  );
}

/**
 * Share-to-chat modal: recent chats, search, copy link, send.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @param {"post"|"event"} props.contentType
 * @param {object} props.content
 * @param {object} [props.comment]
 * @returns {JSX.Element}
 */
function ShareModal({
  open,
  onOpenChange,
  userId,
  user,
  contentType,
  content,
  comment,
}) {
  const [query, setQuery] = useState("");
  const [chats, setChats] = useState([]);
  const [connectionIds, setConnectionIds] = useState(() => new Set());
  const [searchPeople, setSearchPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState(() => ({}));
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const commentId = comment?.id;
  const shareUrl = buildContentShareUrl(contentType, content?.id, commentId);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedMap({});
      setNote("");
      setCopied(false);
      setSending(false);
      setError("");
      setSearchPeople([]);
      return;
    }
    if (!userId) {
      setChats([]);
      setConnectionIds(new Set());
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const orgIds = getShareOrgIds(user);
        const [chatsRes, connectionsRes, ...orgResults] = await Promise.all([
          getChats(userId),
          getUserConnections(userId),
          ...orgIds.map((orgId) => getOrganizationChats(orgId, userId)),
        ]);
        if (cancelled) return;

        const personal = chatsRes?.success && Array.isArray(chatsRes.data) ? chatsRes.data : [];
        const orgChats = orgResults.flatMap((result) =>
          result?.success && Array.isArray(result.data) ? result.data : []
        );
        const byId = new Map();
        for (const chat of [...personal, ...orgChats]) {
          if (chat?.id != null) byId.set(Number(chat.id), chat);
        }
        setChats(sortChatsByRecent([...byId.values()]));

        const ids = new Set();
        const rows = connectionsRes?.success && Array.isArray(connectionsRes.data)
          ? connectionsRes.data
          : [];
        for (const connection of rows) {
          if (connection.status && connection.status !== "active") continue;
          const peerId = peerIdFromConnection(connection, userId);
          if (peerId != null) ids.add(peerId);
        }
        setConnectionIds(ids);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, userId, user]);

  useEffect(() => {
    if (!open || !userId) {
      setSearchPeople([]);
      return;
    }
    const trimmed = query.trim();
    if (!trimmed) {
      setSearchPeople([]);
      return;
    }
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      const res = await searchUsers(userId, trimmed);
      if (cancelled) return;
      const users = res?.success && Array.isArray(res.data)
        ? res.data.filter((item) => item.type === "user")
        : [];
      setSearchPeople(users);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [open, query, userId]);

  const existingDmIds = useMemo(() => dmPeerIds(chats), [chats]);

  const items = useMemo(() => {
    const trimmed = query.trim();
    const chatItems = chats
      .filter((chat) => chatMatchesQuery(chat, trimmed))
      .map((chat) => ({
        key: `chat:${chat.id}`,
        kind: "chat",
        name: chat.name || (chat.chat_type === "group" ? "Group" : "Chat"),
        chat,
      }));
    if (!trimmed) return chatItems;

    const people = searchPeople
      .filter((person) => {
        const id = Number(person.id);
        if (!Number.isFinite(id) || id === Number(userId)) return false;
        if (!connectionIds.has(id)) return false;
        if (existingDmIds.has(id)) return false;
        return true;
      })
      .map((person) => ({
        key: `connection:${person.id}`,
        kind: "connection",
        name: displayPersonName(person),
        user: person,
      }));
    return [...chatItems, ...people];
  }, [chats, query, searchPeople, connectionIds, existingDmIds, userId]);

  const selectedCount = Object.keys(selectedMap).length;

  const toggleSelected = useCallback((item) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[item.key]) delete next[item.key];
      else next[item.key] = item;
      return next;
    });
    setError("");
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
      setError("Could not copy link");
    }
  };

  const handleSend = async () => {
    if (!userId || selectedCount === 0 || sending) return;
    setSending(true);
    setError("");
    const metadata = JSON.stringify(
      buildShareMetadata({ contentType, content, comment })
    );
    const selectedItems = Object.values(selectedMap);
    try {
      for (const item of selectedItems) {
        let chatId = item.chat?.id;
        if (item.kind === "connection") {
          const dmRes = await createDM(userId, item.user.id);
          if (!dmRes?.success || !dmRes.data?.id) {
            throw new Error(dmRes?.error || "Failed to start a chat");
          }
          chatId = dmRes.data.id;
        }
        const msgRes = await createChatMessage(chatId, {
          chat_id: chatId,
          sender_id: userId,
          content: note.trim(),
          message_type: contentType,
          metadata,
        });
        if (!msgRes?.success) {
          throw new Error(msgRes?.error || "Failed to send");
        }
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Share send failed:", err);
      setError(err?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,640px)] w-full max-w-md flex-col gap-0 overflow-hidden p-0">
        <div className="relative shrink-0 border-b border-ui-border px-4 py-3">
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute left-2 top-2 text-ui-muted-foreground"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </DialogClose>
          <DialogTitle className="text-center text-base font-semibold">Share</DialogTitle>
          <DialogDescription className="sr-only">
            Share this {contentType} to a chat or copy the link
          </DialogDescription>
        </div>

        <div className="shrink-0 px-4 py-3">
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
            </svg>
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="h-9 bg-ui-card pl-9"
              aria-label="Search chats"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 list-scrollbar">
          {!userId ? (
            <p className="px-1 py-6 text-center text-sm text-ui-muted-foreground">
              Sign in to send this to a chat.
            </p>
          ) : loading && chats.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-ui-muted-foreground">Loading chats…</p>
          ) : items.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-ui-muted-foreground">
              {query.trim() ? "No matching chats" : "No recent chats yet"}
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-x-2 gap-y-4">
              {items.map((item) => {
                const selected = Boolean(selectedMap[item.key]);
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleSelected(item)}
                    className="flex flex-col items-center gap-1.5 rounded-md px-1 py-1 text-center hover:bg-ui-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
                    aria-pressed={selected}
                  >
                    <ShareTargetAvatar item={item} selected={selected} />
                    <span className="line-clamp-2 w-full text-xs leading-tight text-text-primary">
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error ? (
          <p className="shrink-0 px-4 pb-2 text-sm text-error">{error}</p>
        ) : null}

        <div className="shrink-0 border-t border-ui-border px-4 py-3">
          {selectedCount === 0 ? (
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex w-full items-center gap-3 rounded-md px-1 py-1 text-left hover:bg-ui-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ui-muted text-text-primary">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
                </svg>
              </span>
              <span className="text-sm font-medium text-text-primary">
                {copied ? "Copied!" : "Copy link"}
              </span>
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              <ExpandingTextarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                onSubmitEnter={handleSend}
                placeholder="Write a message..."
                className="bg-ui-background"
              />
              <Button
                type="button"
                className="w-full"
                disabled={!userId || sending}
                onClick={handleSend}
              >
                {sending
                  ? "Sending…"
                  : selectedCount === 1
                    ? "Send"
                    : "Send separately"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareModal;
